//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Actions
import { manageCreateEditManageLocation, reset } from "./redux/slice";

//Controls
import { Button, Form, Input, Modal, Switch } from "@gpstrackit/gtc-react-controls";

//Components
import { NotificationManager, NotificationContainer } from "Components/Notifications";
import InputSearchMap from "Components/Map/components/InputSearchMap";

import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";

const CreateEditManageLocation = ({ refresh }) => {
  //States
  const [formData, setFormData] = useState({ title: null, address: null, enabledELDRule: false });
  const [addressValid, setAddressValid] = useState(null);

  //Language
  const { messages } = useIntl();

  //Form refs
  const createEditLocationFormRef = Form.useForm();

  //Redux
  const dispatch = useDispatch();
  const { location, loading, error, updateManageLocationResult, open } = useSelector(
    (state) => state.manageCreateEditManageLocationRedux
  );

  //Memos
  const title = useMemo(
    () =>
      location?.title
        ? `${messages["manage_createEditLocation_EditTitle"]}: "${location?.title}"`
        : messages["manage_createEditLocation_CreateTitle"],
    [location]
  );

  const titleFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );

  //Handlers and utils
  const validateAndAddStylesError = useCallback(() => {
    const id = "google-map-autocomplete";
    if (
      formData.enabledELDRule &&
      (formData?.address?.address === "" || formData?.address?.address === null)
    ) {
      document.getElementById(id)?.classList.add("form-control-invalid");
      if (!document.getElementById("error-" + id)) {
        document
          .getElementById(id)
          .insertAdjacentHTML(
            "afterend",
            '<small class="text-danger" id="error-' +
              id +
              '" style="padding-top: 5px;">Required field</small>'
          );
      }
      return false;
    } else {
      document.getElementById(id)?.classList.remove("form-control-invalid");
      if (document.getElementById("error-" + id)) {
        document.getElementById("error-" + id).remove();
      }

      if (!addressValid && formData?.address?.address1 === "") {
        return false;
      }

      return true;
    }
  }, [formData]);

  const cleanAddress = (addr) => {
    return addr?.trim().replace(/^,\s*/, "").trim() || "";
  };

  const handleUpdateManageLocationResult = useCallback(() => {
    const addressIsValidate = validateAndAddStylesError();
    if (!addressIsValidate) {
      return;
    }
    const { enabledELDRule, address, latitude, longitude } = formData;
    const title = String(formData.title ?? "").trim();
    const payload = {
      id: location?.id,
      title,
      enabledELDRule: enabledELDRule,
      address:
        formData?.address?.address || formData?.address?.address1 || formData?.address?.fulladdress
          ? {
              address: cleanAddress(address?.address1 || address?.address || address?.fulladdress || ""),
              city: address?.city || "",
              formatted: address?.formatted || "",
              state: address?.state || "",
              zip: address?.postalcode || address?.zip || ""
            }
          : null,
      latitude: latitude || address?.lat || 0,
      longitude: longitude || address?.lng || 0
    };
    dispatch(manageCreateEditManageLocation(payload));
  }, [formData, location]);

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  const handleSuccess = useCallback(() => {
    if (location?.id) {
      NotificationManager.success(
        messages["manage_createEditLocation_success_edit"],
        messages["success"],
        3000,
        null,
        null,
        "success"
      ); 
    } else {
      NotificationManager.success(
        messages["manage_createEditLocation_success"],
        messages["success"],
        3000,
        null,
        null,
        "success"
      );
    }
    
    refresh();
    handleClose();
  }, []);

  useEffect(() => {
    const element = document.getElementById("google-map-autocomplete");
    if (!element) return;
    element.classList.remove("form-control-invalid");
    const errorElement = document.getElementById("error-google-map-autocomplete");
    if (errorElement) {
      errorElement.remove();
    }
    if (!addressValid && formData?.address?.address === "") {
      element.classList.add("form-control-invalid");
      if (document.getElementById("error-google-map-autocomplete")) return;
      element.insertAdjacentHTML(
        "afterend",
        `
            <small class="text-danger" id="error-google-map-autocomplete" style="padding-top: 5px;">${messages["locations_required_address_specify"]}</small>`
      );
    }
  }, [addressValid]);

  useEffect(() => {
    if (!updateManageLocationResult?.id) return;
    handleSuccess();
  }, [updateManageLocationResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1010)) {
      errorMessage = messages["manage_createEditLocation_duplicatedLocationErrorMessage"];
    }
    NotificationManager.error(errorMessage, messages["error"], 4000, null, null, "error");
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!open || !location?.id) return;
    setTimeout(() => {
      const { title, address, enabledELDRule, latitude, longitude } = location;
      const defaultData = {
        title,
        address,
        enabledELDRule,
        latitude,
        longitude
      };
      setFormData(defaultData);
      createEditLocationFormRef.setValues(defaultData);
    });
  }, [location, open]);

  useEffect(() => {
    validateAndAddStylesError();
    const noEmojisRegex = /[\uD83C-\uDBFF\uDC00-\uDFFF]+/g;
    const value = formData.title;
    if (!value) return;
    if (!noEmojisRegex.test(value)) return;
    const noEmojis = value.replace(noEmojisRegex, "");
    const newFormData = { ...formData, title: noEmojis };
    setFormData(newFormData);
    createEditLocationFormRef.setValues(newFormData);
  }, [formData]);

  useEffect(() => {
    return () => reset();
  }, []);

  return (
    <Modal
      title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditLocationFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={createEditLocationFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleUpdateManageLocationResult()}
          onChangeValues={() => {}}
        >
          <Form.Item
            name="title"
            label={messages["manage_createEditLocation_TitleInputLabel"]}
            rules={titleFieldRules}
          >
            <Input
              id="title"
              type="text"
              placeholder={messages["manage_createEditLocation_TitleInputLabel"]}
              onChange={(value) => setFormData({ ...formData, title: value })}
              maxLength={20}
              isDisabled={location?.locationModelId === 2}
            />
          </Form.Item>
          <Form.Item
            name="address"
            label={messages["manage_createEditLocation_AddressInputLabel"]}
            rules={[{ required: formData.enabledELDRule, message: messages["required_field"] }]}
          >
            <InputSearchMap
              className="location-createEdit-addressInput"
              normalView
              noMap
              placeholder={messages["search"]}
              noOptionsText={messages["noOptions"]}
              onGetAddress={(value) => {
                if (value[0].fulladdress === "") {
                  setAddressValid(false);
                  return;
                }
                setAddressValid(true);
                const newFormData = {
                  ...formData,
                  address: value[0],
                  latitude: value[0].lat,
                  longitude: value[0].lng
                };
                createEditLocationFormRef.setValues(newFormData);
                setFormData(newFormData);
              }}
              onClear={() => {
                setAddressValid(true);
                const newFormData = {
                  ...formData,
                  address: {
                    address: "",
                    city: "",
                    formatted: "",
                    state: "",
                    zip: ""
                  },
                  latitude: 0,
                  longitude: 0
                };
                createEditLocationFormRef.setValues(newFormData);
                setFormData(newFormData);
              }}
              draggable={false}
              addressValue={formData?.address?.address || ""}
              onClearSetAddress={() => {}}
              onClearInput={() => {}}
            />
          </Form.Item>
          <Form.Item
            name="enabledELDRule"
            label={messages["manage_createEditLocation_EnabledELDRuleLabel"]}
          >
            <Switch
              id="enabledELDRule"
              optionLabels={[
                messages["manage_locationsTable_activated"],
                messages["manage_locationsTable_deactivated"]
              ]}
              size={"dynamic"}
              onChange={(value) => {
                setFormData({ ...formData, enabledELDRule: value });
              }}
            />
          </Form.Item>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageLocation;
